import { config } from '../config';

const LOG_KEY = 'ls.diagnostics.log.v1';
const META_KEY = 'ls.diagnostics.meta.v1';
const PENDING_CRASH_KEY = 'ls.diagnostics.pending_crash.v1';
const CONSENT_KEY = 'ls.diagnostics.consent.v1';
const SESSION_KEY = 'ls.diagnostics.session.v1';

const MAX_LOG_ENTRIES = 160;
const MAX_LOG_CHARS = 24_000;
const MAX_META_ITEMS = 12;

type LogLevel = 'info' | 'warn' | 'error';

interface LogEntry {
  ts: string;
  level: LogLevel;
  message: string;
}

interface DiagnosticMeta {
  requestIds: string[];
  traceIds: string[];
  deviceIds: string[];
}

export interface DiagnosticCrash {
  name?: string;
  message?: string;
  stack?: string;
  source?: string;
  component?: string;
}

export interface DiagnosticReportPayload {
  kind: 'crash' | 'bug' | 'feedback' | 'support';
  consent: true;
  title: string;
  description: string;
  occurred_at_us: number;
  app: {
    platform: 'web';
    version: string;
    build: string;
    os: string;
    device: string;
    locale: string;
    timezone: string;
    route: string;
    user_agent: string;
    app_session: string;
  };
  crash?: DiagnosticCrash;
  device_ids: string[];
  request_ids: string[];
  trace_ids: string[];
  app_log: string;
  attachments?: Array<Omit<DiagnosticAttachmentPayload, 'byteLength'>>;
}

export interface DiagnosticAttachmentPayload {
  filename: string;
  content_type: 'image/jpeg' | 'image/png' | 'image/webp';
  data: string;
  byteLength: number;
}

export class DiagnosticAttachmentError extends Error {
  constructor(public readonly code: 'unsupported_type' | 'invalid_size' | 'invalid_image') {
    super(code);
    this.name = 'DiagnosticAttachmentError';
  }
}

export interface DiagnosticSubmitResult {
  report_id: string;
  fingerprint: string;
  status: string;
  deduplicated: boolean;
  issue?: { iid?: number; url?: string };
}

let installed = false;

export function installDiagnostics(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  ensureSessionId();

  (['info', 'warn', 'error'] as const).forEach((level) => {
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      recordDiagnosticLog(level, args.map(stringifyLogArg).join(' '));
      original(...args);
    };
  });

  window.addEventListener('error', (event) => {
    const error = event.error instanceof Error ? event.error : undefined;
    const crash: DiagnosticCrash = {
      name: error?.name ?? 'Error',
      message: error?.message ?? event.message,
      stack: error?.stack,
      source: event.filename ? `${event.filename}:${event.lineno}:${event.colno}` : 'window.error',
    };
    storePendingCrash(crash);
    recordDiagnosticLog('error', `uncaught ${crash.name ?? 'Error'} ${crash.message ?? ''}`);
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const crash: DiagnosticCrash =
      reason instanceof Error
        ? { name: reason.name, message: reason.message, stack: reason.stack, source: 'unhandledrejection' }
        : { name: 'UnhandledRejection', message: stringifyLogArg(reason), source: 'unhandledrejection' };
    storePendingCrash(crash);
    recordDiagnosticLog('error', `unhandledrejection ${crash.message ?? ''}`);
  });
}

export function getDiagnosticConsent(): boolean {
  return storageGet(CONSENT_KEY) === '1';
}

export function setDiagnosticConsent(on: boolean): void {
  storageSet(CONSENT_KEY, on ? '1' : '0');
}

export function getPendingCrash(): DiagnosticCrash | null {
  const raw = storageGet(PENDING_CRASH_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { crash?: DiagnosticCrash };
    return parsed.crash ?? null;
  } catch {
    return null;
  }
}

export function clearPendingCrash(): void {
  storageRemove(PENDING_CRASH_KEY);
}

export function recordDiagnosticLog(level: LogLevel, message: string): void {
  const clean = redact(message).slice(0, 1200);
  const entries = readJSON<LogEntry[]>(LOG_KEY, []);
  entries.push({ ts: new Date().toISOString(), level, message: clean });
  storageSet(LOG_KEY, JSON.stringify(entries.slice(-MAX_LOG_ENTRIES)));
}

export function recordApiExchange(input: {
  path: string;
  method: string;
  status: number;
  requestId: string | null;
  traceId: string | null;
}): void {
  const meta = readJSON<DiagnosticMeta>(META_KEY, { requestIds: [], traceIds: [], deviceIds: [] });
  if (input.requestId) meta.requestIds = pushRecent(meta.requestIds, input.requestId);
  if (input.traceId) meta.traceIds = pushRecent(meta.traceIds, input.traceId);
  const deviceId = deviceIdFromPath(input.path);
  if (deviceId) meta.deviceIds = pushRecent(meta.deviceIds, deviceId, 8);
  storageSet(META_KEY, JSON.stringify(meta));
  if (input.status >= 400) {
    recordDiagnosticLog('warn', `api ${input.method} ${input.path} status=${input.status} request_id=${input.requestId ?? '-'} trace_id=${input.traceId ?? '-'}`);
  }
}

export function buildDiagnosticReportPayload(note: string): DiagnosticReportPayload {
  const crash = getPendingCrash();
  const meta = readJSON<DiagnosticMeta>(META_KEY, { requestIds: [], traceIds: [], deviceIds: [] });
  const appLog = readLogText();
  const title = crash ? `${crash.name ?? 'Crash'}: ${crash.message ?? 'app crash'}` : 'User diagnostic report';
  return {
    kind: crash ? 'crash' : 'feedback',
    consent: true,
    title: title.slice(0, 160),
    description: redact(note).slice(0, 4000),
    occurred_at_us: Date.now() * 1000,
    app: {
      platform: 'web',
      version: config.appVersion,
      build: typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : 'dev',
      os: navigator.platform || '',
      device: screen ? `${screen.width}x${screen.height}` : '',
      locale: navigator.language || '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      route: window.location.pathname + window.location.search,
      user_agent: navigator.userAgent,
      app_session: ensureSessionId(),
    },
    ...(crash ? { crash } : {}),
    device_ids: meta.deviceIds,
    request_ids: meta.requestIds,
    trace_ids: meta.traceIds,
    app_log: appLog,
  };
}

export function buildSupportReportPayload(input: {
  subject: string; description: string; deviceId?: string; attachments?: DiagnosticAttachmentPayload[];
}): DiagnosticReportPayload {
  const payload = buildDiagnosticReportPayload(input.description);
  payload.kind = 'support';
  payload.title = input.subject.slice(0, 160);
  payload.description = input.description.slice(0, 4000);
  payload.device_ids = input.deviceId ? [input.deviceId] : [];
  delete payload.crash;
  payload.attachments = input.attachments?.map((attachment) => ({
    filename: attachment.filename,
    content_type: attachment.content_type,
    data: attachment.data,
  }));
  return payload;
}

export async function fileToDiagnosticAttachment(file: File): Promise<DiagnosticAttachmentPayload> {
  const allowed = new Set(['image/jpeg', 'image/png', 'image/webp']);
  if (!allowed.has(file.type)) throw new DiagnosticAttachmentError('unsupported_type');
  if (file.size <= 0 || file.size > 2 * 1024 * 1024) throw new DiagnosticAttachmentError('invalid_size');
  let source: CanvasImageSource;
  let width: number;
  let height: number;
  let close: () => void;
  try {
    if (typeof createImageBitmap === 'function') {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      source = bitmap; width = bitmap.width; height = bitmap.height; close = () => bitmap.close();
    } else {
      const objectURL = URL.createObjectURL(file);
      const image = new Image();
      image.src = objectURL;
      await image.decode();
      source = image; width = image.naturalWidth; height = image.naturalHeight;
      close = () => URL.revokeObjectURL(objectURL);
    }
  } catch {
    throw new DiagnosticAttachmentError('invalid_image');
  }
  if (width <= 0 || height <= 0) {
    close();
    throw new DiagnosticAttachmentError('invalid_image');
  }

  try {
    const scale = Math.min(1, 2048 / Math.max(width, height));
    let targetWidth = Math.max(1, Math.round(width * scale));
    let targetHeight = Math.max(1, Math.round(height * scale));
    for (let resize = 0; resize < 4; resize += 1) {
      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const context = canvas.getContext('2d');
      if (!context) throw new DiagnosticAttachmentError('invalid_image');
      context.fillStyle = '#fff';
      context.fillRect(0, 0, targetWidth, targetHeight);
      context.drawImage(source, 0, 0, targetWidth, targetHeight);
      for (const quality of [0.86, 0.7, 0.55, 0.4, 0.3]) {
        const blob = await diagnosticCanvasBlob(canvas, quality);
        if (blob.size > 0 && blob.size <= 2 * 1024 * 1024) {
          const bytes = new Uint8Array(await blob.arrayBuffer());
          let binary = '';
          for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
          const stem = file.name.replace(/\\/g, '/').split('/').pop()?.replace(/\.[^.]*$/, '').replace(/[^A-Za-z0-9._-]/g, '').replace(/^\.+|\.+$/g, '') || 'support';
          return { filename: `${stem.slice(0, 115)}.jpg`, content_type: 'image/jpeg', data: btoa(binary), byteLength: bytes.length };
        }
      }
      targetWidth = Math.max(1, Math.round(targetWidth * 0.7));
      targetHeight = Math.max(1, Math.round(targetHeight * 0.7));
    }
  } catch (error) {
    if (error instanceof DiagnosticAttachmentError) throw error;
    throw new DiagnosticAttachmentError('invalid_image');
  } finally {
    close();
  }
  throw new DiagnosticAttachmentError('invalid_size');
}

function diagnosticCanvasBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('image encode failed')), 'image/jpeg', quality);
  });
}

function storePendingCrash(crash: DiagnosticCrash): void {
  storageSet(PENDING_CRASH_KEY, JSON.stringify({ occurred_at_us: Date.now() * 1000, crash: redactCrash(crash) }));
}

function redactCrash(crash: DiagnosticCrash): DiagnosticCrash {
  return {
    name: crash.name ? redact(crash.name).slice(0, 160) : undefined,
    message: crash.message ? redact(crash.message).slice(0, 2000) : undefined,
    stack: crash.stack ? redact(crash.stack).slice(0, 24_000) : undefined,
    source: crash.source ? redact(crash.source).slice(0, 240) : undefined,
    component: crash.component ? redact(crash.component).slice(0, 120) : undefined,
  };
}

function readLogText(): string {
  const entries = readJSON<LogEntry[]>(LOG_KEY, []);
  const text = entries.map((e) => `${e.ts} ${e.level} ${e.message}`).join('\n');
  return text.length > MAX_LOG_CHARS ? text.slice(text.length - MAX_LOG_CHARS) : text;
}

function stringifyLogArg(arg: unknown): string {
  if (arg instanceof Error) return `${arg.name}: ${arg.message}\n${arg.stack ?? ''}`.trim();
  if (typeof arg === 'string') return arg;
  try {
    return JSON.stringify(arg);
  } catch {
    return String(arg);
  }
}

function deviceIdFromPath(path: string): string | null {
  const match = path.match(/\/v3\/devices\/([^/?]+)/);
  if (!match) return null;
  const id = decodeURIComponent(match[1]);
  return id === 'binding' ? null : id;
}

function pushRecent(xs: string[], v: string, max = MAX_META_ITEMS): string[] {
  return [v, ...xs.filter((x) => x !== v)].slice(0, max);
}

function ensureSessionId(): string {
  const existing = storageGet(SESSION_KEY);
  if (existing) return existing;
  const id = randomId();
  storageSet(SESSION_KEY, id);
  return id;
}

function randomId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `diag-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function readJSON<T>(key: string, fallback: T): T {
  const raw = storageGet(key);
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function storageGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage can be unavailable in private mode. Diagnostics are best-effort.
  }
}

function storageRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // best-effort
  }
}

const redactors: Array<[RegExp, string]> = [
  [/authorization:\s*bearer\s+[A-Za-z0-9._~+/=-]+/gi, 'authorization: Bearer [REDACTED]'],
  [/bearer\s+[A-Za-z0-9._~+/=-]+/gi, 'Bearer [REDACTED]'],
  [/(access_token|refresh_token|id_token|client_key|password|owner_token)=([^&\s]+)/gi, '$1=[REDACTED]'],
  [/("(?:access_token|refresh_token|id_token|client_key|password|owner_token)"\s*:\s*)"[^"]*"/gi, '$1"[REDACTED]"'],
  [/[A-Za-z0-9_-]{20,}\.[A-Za-z0-9_-]{20,}\.[A-Za-z0-9_-]{20,}/g, '[JWT_REDACTED]'],
];

function redact(input: string): string {
  return redactors.reduce((out, [re, to]) => out.replace(re, to), input);
}
